import { Button } from '@material-ui/core'
import React, {
    FC,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import Skeleton from '@material-ui/lab/Skeleton'
import { useFormContext, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { UsersByExtension } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LicenceResponseMapped, SessionAPIResponse } from '../../../NonReact/Types/Types'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { IsStringEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { ReportingAccessLevels } from '../../AddUser/Reporting/ReportingAccessLevels'
import { RESTAPIError } from '../../Common/API'
import { getSession } from '../../Common/API/CommonAPI'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { StatusCodes } from '../../../NonReact/common/API/REST/RESTAPIClient'
import Checkbox from '../../Common/Components/Checkbox/Checkbox'
import Icon, { IconName } from '../../Common/Components/Icon'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import PopperBubble from '../../Common/Components/PopperBubble'
import Select from '../../Common/Components/Selects/Select'
import { DeviceSelectItem, SelectOption, SelectOptions } from '../../Common/Components/Selects/Types'
import TextInput from '../../Common/Components/TextInput'
import { CommonFormContext, FormContextType } from '../../Common/Contexts/CommonFormContext'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { UserDefault, UserDefaults } from '../../Common/Types'
import { ReactState } from '../../Common/Types/Types'
import { LanguageUtils } from '../../Common/Utils/LanguageUtils'
import { isEmailAddressValid } from '../../Common/Utils/ValidityUtils'
import { UserTypes } from '../../SelfProvisioning/Constants/Constants'
import { userFormStateMapper } from '../../SelfProvisioning/Mappers/UserMapper'
import userDefaults from '../Config/UserDefaults.json'
import { LicenceTypeFieldValueMap, UserDefaultNames } from '../Constants/Constants'
import {
    getCxiLicenceFieldName,
    getUseEmailAsUsernameFieldName,
    getUserEmailFieldName,
    getUserTypeFieldName,
    getWelcomeEmailFieldName,
    USE_EMAIL_AS_USERNAME_FIELD_NAME
} from '../Constants/FieldNames'
import { CxIntegrationSelectListItem, UsersByExtensionForm } from '../Types/Types'
import { EntryRemove } from './EntryRemove'
import { useMemoRef } from '../../Common/Hooks/useMemoRef'
import { ReactStateRef, useStateAsRef } from '../../Common/Hooks/useStateAsRef'
import { EmailType, sendEmailForUser } from '../../Common/API/UserAPIUtilities'
import { displayErrorMessage, displaySuccessMessage } from '../../Common/Utils/Messages'
import { PromptContext } from '../../Common/Contexts'
import { PromptDisplayType } from '../../Common/Contexts/PromptContext'
import { AdminUserBody, ApplicationUserResponse, JSONResponse } from '../../../NonReact/common/Types/Types'
import { createUser, getUser, updateUser } from '../../../NonReact/common/API/EntityAPIClient'
import { ApplicationUser } from '../../../NonReact/common/Types/AddUser/AddUserTypes'
import { CxIntegrationLicence } from '../../../NonReact/common/Types/SelfProvisioning/SelfProvisioningTypes'
import { PARTITION_FILTER_NAME, TELSYS_FILTER_NAME } from '../../InsightsHub/Constants/FieldNames'
import { getCMEIDFromTelSys } from '../../Common/API/ItemGetters'
import { DEFAULT_CME_ID } from '../../Common/Constants/ServerValues'

type Props = {
    extension: DeviceSelectItem
    cmeID: string
    extensionError: Definable<string>
    updateDeviceUsers: (index: number, newUsersValue: string[]) => void
    updateExtensionErrors: (index: number, newErrors: Definable<string>) => void
    historicOnly?: boolean
    telsysType?: TelSysTypes
    licenceSummary: Nullable<LicenceResponseMapped>
    updateLicenceSummary: () => void
    isAdminProvisioningUser: boolean
    shouldShowReportingTypeColumn: boolean
    shouldShowCXIntegrationColumn: boolean
    shouldShowWelcomeMailColumn: boolean
    availableTelServers?: SelectOptions<number>
}

export const UserByExtensionEntry: FC<Props> = ({
    extension: { label, index, original: originalDevice, isAgent },
    cmeID,
    historicOnly,
    extensionError,
    updateDeviceUsers,
    updateExtensionErrors,
    telsysType,
    licenceSummary,
    updateLicenceSummary,
    isAdminProvisioningUser,
    shouldShowReportingTypeColumn,
    shouldShowCXIntegrationColumn,
    shouldShowWelcomeMailColumn,
    availableTelServers
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()

    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)
    const { displayError }: MessageContextType = useContext(MessageContext)
    const formContext: Nullable<FormContextType> = useContext(CommonFormContext)

    const {
        watch,
        setValue,
        formState: { dirtyFields }
    }: UseFormMethods<UsersByExtensionForm> = useFormContext()

    const currentIndex: number = index ?? 0

    const isWebexCalling: boolean = telsysType === TelSysTypes.WEBEX_CALLING

    const userEmailFieldName: string = getUserEmailFieldName(currentIndex)
    const userTypeFieldName: string = getUserTypeFieldName(currentIndex)
    const useEmailAsUsernameFieldName: string = getUseEmailAsUsernameFieldName(currentIndex)
    const welcomeEmailFieldName: string = getWelcomeEmailFieldName(currentIndex)
    const cxiLicenceFieldName: string = getCxiLicenceFieldName(currentIndex)

    const userEmailValue: string = watch(userEmailFieldName, EMPTY_STRING)
    const userTypeValue: string = watch(userTypeFieldName, UserTypes.ESSENTIALS)
    const useEmailAsUsernameValue: boolean = watch(useEmailAsUsernameFieldName, false)
    const cxiLicenceValue: CxIntegrationLicence = watch(cxiLicenceFieldName, CxIntegrationLicence.NONE)
    const welcomeEmailValue: boolean = watch(welcomeEmailFieldName, true)

    const telSysValue: number = watch(
        TELSYS_FILTER_NAME,
        availableTelServers ? availableTelServers[0]?.value : ALL_VALUE
    )
    const partitionValue: number = watch(PARTITION_FILTER_NAME)

    const cxIntegrationLicences: CxIntegrationSelectListItem[] = useMemo(
        (): CxIntegrationSelectListItem[] => [
            {
                id: CxIntegrationLicence.NONE,
                friendlyName: language.getWithTelSysCheck(
                    UsersByExtension.UsersByExtension_CxiLicenceType_NONE,
                    telsysType
                )
            },
            {
                id: CxIntegrationLicence.SOLO,
                friendlyName: language.getWithTelSysCheck(
                    UsersByExtension.UsersByExtension_CxiLicenceType_SOLO,
                    telsysType
                )
            },
            {
                id: CxIntegrationLicence.TEAMS,
                friendlyName: language.getWithTelSysCheck(
                    UsersByExtension.UsersByExtension_CxiLicenceType_TEAMS,
                    telsysType
                )
            },
            {
                id: CxIntegrationLicence.CXI,
                friendlyName: language.getWithTelSysCheck(
                    UsersByExtension.UsersByExtension_CxiLicenceType_CX_INTEGRATION,
                    telsysType
                )
            }
        ],
        []
    )

    const isCxiLicenceType: boolean = useMemo(
        (): boolean => (cxiLicenceValue?.toUpperCase() as CxIntegrationLicence) === CxIntegrationLicence.CXI,
        [cxiLicenceValue]
    )

    const [subscriptionTypesExtensionOptions, setSubscriptionTypesExtensionOptions]: ReactState<SelectOptions<string>> =
        useState<SelectOptions<string>>([
            {
                label: language.getWithTelSysCheck(UsersByExtension.UsersByExtension_UserType_ESSENTIALS, telSysValue),
                value: UserTypes.ESSENTIALS
            },
            {
                label: language.getWithTelSysCheck(
                    UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_AGENT,
                    telsysType
                ),
                value: UserTypes.CALL_CENTRE_AGENT
            },
            {
                label: language.getWithTelSysCheck(
                    UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_SUPERVISOR,
                    telsysType
                ),
                value: UserTypes.CALL_CENTRE_SUPERVISOR
            },
            ...(isWebexCalling
                ? []
                : [
                      {
                          label: language.get(UsersByExtension.UsersByExtension_UserType_PROFESSIONAL),
                          value: UserTypes.PROFESSIONAL
                      },
                      {
                          label: language.get(UsersByExtension.UsersByExtension_UserType_ENTERPRISE),
                          value: UserTypes.ENTERPRISE
                      }
                  ]),
            {
                label: language.get(UsersByExtension.UsersByExtension_UserType_NONE),
                value: UserTypes.NONE
            }
        ])

    const [isDataLoading, setIsDataLoading]: ReactState<boolean> = useState<boolean>(false)

    const [confirmDeleteIndex, setConfirmDeleteIndex]: ReactState<Definable<number>> =
        useState<Definable<number>>(undefined)

    const [isEditExisting, setIsEditExisting]: ReactState<boolean> = useState<boolean>(false)

    const [additionalSubscriptionTypes, setAdditionalSubscriptionTypes]: ReactState<Definable<SelectOption<string>>> =
        useState<Definable<SelectOption<string>>>()

    const [currentUsername, setCurrentUsername]: ReactStateRef<Definable<string>> =
        useStateAsRef<Definable<string>>(undefined)
    const [currentEmail, setCurrentEmail]: ReactState<Definable<string>> = useState<Definable<string>>(undefined)
    const [currentType, setCurrentType]: ReactState<Definable<string>> = useState<Definable<string>>(undefined)
    const [currentCxiLicence, setCurrentCxiLicence]: ReactState<Definable<CxIntegrationLicence>> =
        useState<Definable<CxIntegrationLicence>>(undefined)
    const [confirmDelete, setConfirmDelete]: ReactState<boolean> = useState<boolean>(false)
    const [licenceTypePopperOpen, setLicenceTypePopperOpen]: ReactState<boolean> = useState<boolean>(false)
    const [checkboxPopperOpen, setCheckboxPopperOpen]: ReactState<boolean> = useState<boolean>(false)

    const isEmailValid: boolean = useMemo((): boolean => isEmailAddressValid(userEmailValue), [userEmailValue])
    const isUserTypeValid: boolean = useMemo(
        (): boolean =>
            userTypeValue === UserTypes.NONE ||
            userTypeValue === UserTypes.ESSENTIALS ||
            userTypeValue === UserTypes.PROFESSIONAL ||
            userTypeValue === UserTypes.ENTERPRISE ||
            userTypeValue === UserTypes.CALL_CENTRE_AGENT ||
            userTypeValue === UserTypes.CALL_CENTRE_SUPERVISOR,
        [userTypeValue]
    )
    const isMonitoredDeactivated: boolean = useMemo((): boolean => userTypeValue === UserTypes.NONE, [userTypeValue])
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const formDirtyFields: Nullable<Record<string, string | boolean>> = formContext?.dirtyFields

    const isTypeTouched: boolean = useMemo(
        (): boolean => !!formDirtyFields?.[userTypeFieldName],
        [JSON.stringify(formDirtyFields)]
    )

    const isCrmTouched: boolean = useMemo(
        (): boolean => !!formDirtyFields?.[cxiLicenceFieldName],
        [JSON.stringify(formDirtyFields)]
    )

    const currentSubscriptionOptions: SelectOptions<string> = useMemo(
        (): SelectOptions<string> => [
            ...subscriptionTypesExtensionOptions,
            ...(additionalSubscriptionTypes ? [additionalSubscriptionTypes] : [])
        ],
        [isAgent, JSON.stringify(additionalSubscriptionTypes), JSON.stringify(subscriptionTypesExtensionOptions)]
    )

    const idOnTelSysToUser: string = useMemo(
        (): string => originalDevice?.TelSysUsername ?? EMPTY_STRING,
        [originalDevice]
    )
    const popperRef: MutableRefObject<null> = useRef(null)
    const licenceTypePopperRef: MutableRefObject<null> = useRef(null)
    const checkboxPopperRef: MutableRefObject<null> = useRef(null)
    const [isPopperOpen, setIsPopperOpen]: ReactState<boolean> = useState<boolean>(false)

    const isCurrentUserDelete: boolean = useMemo(
        (): boolean =>
            (originalDevice?.Users?.length ?? 0) > 0 &&
            userEmailValue === currentEmail &&
            userTypeValue === currentType &&
            cxiLicenceValue?.toUpperCase() === currentCxiLicence?.toUpperCase(),
        [
            userEmailValue,
            userTypeValue,
            currentEmail,
            currentType,
            originalDevice?.Users?.length ?? 0,
            currentCxiLicence,
            cxiLicenceValue
        ]
    )

    const userTypeDefaults: Definable<UserDefault> = useMemo(
        (): Definable<UserDefault> => (userDefaults as unknown as Record<string, UserDefault>)?.[userTypeValue],
        [userDefaults, userTypeValue]
    )

    const usernameForUser: MutableRefObject<string> = useMemoRef((): string => {
        const isUpgradeToTopLevel: boolean =
            !!currentType &&
            (currentType === UserTypes.ESSENTIALS ||
                currentType === UserTypes.PROFESSIONAL ||
                currentType === UserTypes.CALL_CENTRE_AGENT) &&
            (userTypeValue === UserTypes.ENTERPRISE || userTypeValue === UserTypes.CALL_CENTRE_SUPERVISOR)
        const normalUsername: string = !useEmailAsUsernameValue ? idOnTelSysToUser ?? '' : userEmailValue
        const upgradeUserName: string = isUpgradeToTopLevel ? currentUsername.current ?? normalUsername : normalUsername
        const fixBlankUsername: string =
            IsStringEmpty(upgradeUserName) && useEmailAsUsernameValue && isStringNotEmpty(idOnTelSysToUser)
                ? idOnTelSysToUser ?? ''
                : upgradeUserName
        const userName: string = isCurrentUserDelete ? currentUsername.current ?? fixBlankUsername : fixBlankUsername

        if (IsStringEmpty(userName) && isStringNotEmpty(currentUsername.current)) {
            return currentUsername.current
        }

        return userName
    }, [
        userEmailValue,
        useEmailAsUsernameValue,
        userTypeDefaults,
        currentEmail,
        currentType,
        originalDevice,
        idOnTelSysToUser
    ])

    const emailInputDisabled: boolean = useMemo(
        (): boolean => (originalDevice?.Users?.length ?? 0) > 0 && !isEditExisting,
        [originalDevice, isEditExisting]
    )

    const isCxiOnlyUser: boolean = isCxiLicenceType && userTypeValue === UserTypes.NONE

    const mappedUserTypeFromTelsysType: Record<string, string> = {
        [`${UserTypes.CALL_CENTRE_SUPERVISOR}:${TelSysTypes.BROADSOFT_BROADWORKS}`]:
            UserDefaultNames.CALL_CENTRE_SUPERVISOR,
        [`${UserTypes.CALL_CENTRE_SUPERVISOR}:${TelSysTypes.MS_TEAMS}`]: UserDefaultNames.CXA_ADVANCED,
        [`${UserTypes.CALL_CENTRE_SUPERVISOR}:${TelSysTypes.WEBEX_CALLING}`]: UserDefaultNames.WXC_SUPERVISOR
    }

    const addModifyUser = useCallback(async (): Promise<void> => {
        if (!isEmailValid) return

        setIsDataLoading(true)

        const userType: string =
            mappedUserTypeFromTelsysType[`${watch(userTypeFieldName, UserTypes.ESSENTIALS)}:${telsysType}`] ??
            watch(userTypeFieldName, UserTypes.ESSENTIALS)

        const refetchedCMEID: string = await getCMEIDFromTelSys(telSysValue)
            .then((id: Definable<string>): string => (isVarNotEmpty(id) ? id : DEFAULT_CME_ID))
            .catch((): string => DEFAULT_CME_ID)

        getSession()
            .then(async (session: JSONResponse<SessionAPIResponse>): Promise<void> => {
                if (isVarNotEmpty(session?.Nonce)) {
                    const user: AdminUserBody = await userFormStateMapper(
                        userDefaults as unknown as UserDefaults,
                        {
                            email: watch(userEmailFieldName, EMPTY_STRING),
                            name: originalDevice?.Description ?? EMPTY_STRING,
                            userType,
                            welcomeEmail: shouldShowWelcomeMailColumn ? watch(welcomeEmailFieldName, true) : false,
                            cxiLicence: watch(cxiLicenceFieldName, CxIntegrationLicence.NONE)
                        },
                        refetchedCMEID,
                        telSysValue ?? ALL_VALUE,
                        partitionValue ?? ALL_VALUE,
                        session?.Nonce,
                        originalDevice,
                        isAgent,
                        currentUsername.current,
                        usernameForUser.current
                    )

                    if (IsStringEmpty(currentUsername.current)) {
                        createUser(user)
                            .then((): void => {
                                if (isMounted.current) {
                                    updateLicenceSummary()
                                    updateDeviceUsers(currentIndex, [...(originalDevice?.Users ?? []), user.Username])
                                    updateExtensionErrors(currentIndex, undefined)

                                    setAdditionalSubscriptionTypes(undefined)
                                    setIsEditExisting(false)
                                    setTimeout((): void => {
                                        if (isMounted.current) {
                                            setIsDataLoading(false)
                                        }
                                    })
                                }
                            })
                            .catch((error: RESTAPIError): void => {
                                if (isMounted.current) {
                                    if (error.HTTPStatus === StatusCodes.OK) {
                                        updateLicenceSummary()
                                        updateDeviceUsers(currentIndex, [
                                            ...(originalDevice?.Users ?? []),
                                            user.Username
                                        ])

                                        setAdditionalSubscriptionTypes(undefined)
                                        setIsEditExisting(false)
                                    }

                                    updateExtensionErrors(currentIndex, error.message)

                                    setTimeout((): void => {
                                        if (isMounted.current) {
                                            setIsDataLoading(false)
                                        }
                                    })
                                }
                            })
                    } else {
                        updateUser(currentUsername.current ?? EMPTY_STRING, user)
                            .then((): void => {
                                if (isMounted.current) {
                                    updateLicenceSummary()
                                    updateDeviceUsers(
                                        currentIndex,
                                        [...(originalDevice?.Users ?? [])].map((entry: string): string =>
                                            entry === currentUsername.current
                                                ? user?.UsernameNew ?? currentUsername.current
                                                : entry
                                        )
                                    )
                                    updateExtensionErrors(currentIndex, undefined)

                                    setAdditionalSubscriptionTypes(undefined)

                                    setIsEditExisting(false)
                                    setTimeout((): void => {
                                        if (isMounted.current) {
                                            setIsDataLoading(false)
                                        }
                                    })
                                }
                            })
                            .catch((error: RESTAPIError): void => {
                                updateLicenceSummary()

                                if (error.HTTPStatus === StatusCodes.OK) {
                                    updateDeviceUsers(
                                        currentIndex,
                                        [...(originalDevice?.Users ?? [])].map((entry: string): string =>
                                            entry === currentUsername.current
                                                ? user?.UsernameNew ?? currentUsername.current
                                                : entry
                                        )
                                    )
                                }

                                updateExtensionErrors(currentIndex, error.message)

                                if (isMounted.current) {
                                    setAdditionalSubscriptionTypes(undefined)
                                    setIsEditExisting(false)
                                    setTimeout((): void => {
                                        if (isMounted.current) {
                                            setIsDataLoading(false)
                                        }
                                    })
                                }
                            })
                    }
                }
            })
            .catch((error: Error): void => {
                updateExtensionErrors(currentIndex, error.message)

                if (isMounted.current) {
                    setTimeout((): void => {
                        if (isMounted.current) {
                            setIsDataLoading(false)
                        }
                    })
                }
            })
    }, [cmeID, telSysValue, usernameForUser.current, originalDevice, telsysType, partitionValue, isEmailValid])

    const submitOnCtrlEnter = (event: React.KeyboardEvent<Element>): void => {
        if (event?.ctrlKey && event?.key === 'Enter') {
            addModifyUser()
        }
    }

    const fetchUser = (userName: string, activateEditing?: boolean): void => {
        setIsDataLoading(true)
        getUser(userName)
            .then((response: JSONResponse<ApplicationUserResponse>): void => {
                if (isMounted.current) {
                    const user: Nullable<ApplicationUser> = response?.Data
                    const userType: string = user?.Scope?.ReportingAccess ?? EMPTY_STRING
                    const userTypeFieldMapValue: string = LicenceTypeFieldValueMap[userType] ?? EMPTY_STRING

                    setValue(userEmailFieldName, user?.EmailAddr ?? EMPTY_STRING)
                    setValue(
                        cxiLicenceFieldName,
                        user?.Scope?.CXILicenseType?.toUpperCase() ?? CxIntegrationLicence.NONE
                    )

                    if (
                        !currentSubscriptionOptions?.some(
                            (option: SelectOption<string>): boolean => option.value === userTypeFieldMapValue
                        )
                    ) {
                        setAdditionalSubscriptionTypes({
                            label:
                                language.getInterpolated(
                                    UsersByExtension.UsersByExtensions_UserFields_LegacyAppend,
                                    language.get(
                                        (UsersByExtension as unknown as Record<string, string>)[
                                            `UsersByExtensions_UserType_Legacy_${userType}`
                                        ]
                                    )
                                ) ?? userType,
                            value: userTypeFieldMapValue
                        })
                    }

                    setTimeout((): void => {
                        setValue(userTypeFieldName, userTypeFieldMapValue ?? EMPTY_STRING)

                        setCurrentUsername(user?.Username ?? EMPTY_STRING)
                        setCurrentEmail(user?.EmailAddr ?? EMPTY_STRING)
                        setCurrentType(userTypeFieldMapValue ?? EMPTY_STRING)
                        setCurrentCxiLicence(user?.Scope?.CXILicenseType ?? CxIntegrationLicence.NONE)

                        setIsEditExisting(!!activateEditing)
                        setIsDataLoading(false)
                    })
                }
            })
            .catch((error: Error): void => {
                displayError(error)

                if (isMounted.current) {
                    setTimeout((): void => {
                        if (isMounted.current) {
                            setIsDataLoading(false)
                        }
                    })
                }
            })
    }

    const createCXIntegrationColumn: () => Nullable<ReactElement> = (): Nullable<ReactElement> => {
        if (!shouldShowCXIntegrationColumn) return null

        if (telsysType === TelSysTypes.MS_TEAMS || telsysType === TelSysTypes.WEBEX_CALLING) {
            return (
                <div className='ube-entry-field crm-licence-checkbox'>
                    <Checkbox
                        isChecked={isCxiLicenceType}
                        disabled={isDataLoading}
                        defaultChecked={false}
                        onChange={(): void => {
                            setValue(
                                cxiLicenceFieldName,
                                isCxiLicenceType ? CxIntegrationLicence.NONE : CxIntegrationLicence.CXI
                            )
                            if (formContext?.handleFormChange) {
                                formContext?.handleFormChange(cxiLicenceFieldName)
                            }
                        }}
                    />
                </div>
            )
        }

        return (
            <div className='ube-entry-field'>
                <Select
                    name={cxiLicenceFieldName}
                    defaultValue={CxIntegrationLicence.NONE}
                    disabled={isDataLoading}
                    onKeyDown={submitOnCtrlEnter}
                    options={cxIntegrationLicences.map(
                        (licence): SelectOption<CxIntegrationLicence> => ({
                            label: licence.friendlyName,
                            value: licence.id
                        })
                    )}
                />
            </div>
        )
    }

    useEffect((): void => {
        if (isMounted.current) {
            setSubscriptionTypesExtensionOptions([
                ...(((licenceSummary?.AvailableLicences?.Essentials ?? 1) <= 0 &&
                    userTypeValue !== UserTypes.ESSENTIALS) ||
                (telsysType === TelSysTypes.MS_TEAMS && !historicOnly)
                    ? []
                    : [
                          {
                              label: language.getWithTelSysCheck(
                                  UsersByExtension.UsersByExtension_UserType_ESSENTIALS,
                                  telsysType
                              ),
                              value: UserTypes.ESSENTIALS
                          }
                      ]),
                ...(((licenceSummary?.AvailableLicences?.Professional ?? 1) <= 0 &&
                    userTypeValue !== UserTypes.PROFESSIONAL) ||
                telsysType === TelSysTypes.MS_TEAMS ||
                telsysType === TelSysTypes.WEBEX_CALLING
                    ? []
                    : [
                          {
                              label: language.getWithTelSysCheck(
                                  UsersByExtension.UsersByExtension_UserType_PROFESSIONAL,
                                  telsysType
                              ),
                              value: UserTypes.PROFESSIONAL
                          }
                      ]),
                ...(((licenceSummary?.AvailableLicences?.Enterprise ?? 1) <= 0 &&
                    userTypeValue !== UserTypes.ENTERPRISE) ||
                (isAdminProvisioningUser && !historicOnly) ||
                telsysType === TelSysTypes.WEBEX_CALLING
                    ? []
                    : [
                          {
                              label: language.getWithTelSysCheck(
                                  UsersByExtension.UsersByExtension_UserType_ENTERPRISE,
                                  telsysType
                              ),
                              value: UserTypes.ENTERPRISE
                          }
                      ]),
                ...(((licenceSummary?.AvailableLicences?.CallCentreAgent ?? 1) <= 0 &&
                    userTypeValue !== UserTypes.CALL_CENTRE_AGENT) ||
                (!isAgent &&
                    [TelSysTypes.MS_TEAMS, TelSysTypes.BROADSOFT_BROADWORKS, TelSysTypes.WEBEX_CALLING].includes(
                        telsysType ?? (EMPTY_STRING as TelSysTypes)
                    ))
                    ? []
                    : [
                          {
                              label: language.getWithTelSysCheck(
                                  UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_AGENT,
                                  telsysType
                              ),
                              value: UserTypes.CALL_CENTRE_AGENT
                          }
                      ]),
                ...(((licenceSummary?.AvailableLicences?.CallCentreSupervisor ?? 1) <= 0 &&
                    userTypeValue !== UserTypes.CALL_CENTRE_SUPERVISOR) ||
                (telsysType === TelSysTypes.MS_TEAMS && historicOnly)
                    ? []
                    : [
                          {
                              label: language.getWithTelSysCheck(
                                  UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_SUPERVISOR,
                                  telsysType
                              ),
                              value: UserTypes.CALL_CENTRE_SUPERVISOR
                          }
                      ]),
                {
                    label: language.get(UsersByExtension.UsersByExtension_UserType_NONE),
                    value: UserTypes.NONE
                }
            ])
        }
    }, [licenceSummary, userTypeValue, telsysType, historicOnly, isAgent, isAdminProvisioningUser])

    useEffect((): void => {
        if (isMounted.current && (originalDevice?.Users?.length ?? 0) === 1) {
            const userName: string = originalDevice?.Users?.[0] ?? EMPTY_STRING

            fetchUser(userName, false)
        } else {
            setIsDataLoading(false)
        }
    }, [JSON.stringify(originalDevice?.Users)])

    useEffect((): void => {
        if (isCxiOnlyUser && welcomeEmailValue === true) {
            setValue(welcomeEmailFieldName, false)
        }
    }, [isCxiOnlyUser, welcomeEmailValue])

    return (
        <>
            <div className={`ube-entry ${isDataLoading ? 'hide' : ''}`}>
                <div className='ube-main-content'>
                    <div className='ube-entry-extension'>
                        <div
                            className='ube-entry-extension-label'
                            onMouseOver={(): void => setIsPopperOpen(true)}
                            onMouseOut={(): void => setIsPopperOpen(false)}
                            ref={popperRef}
                            title={label.length > 25 ? label : EMPTY_STRING}
                        >
                            <span className='ubeeel-device-number'>{originalDevice?.DeviceNumber}&nbsp;-&nbsp;</span>
                            <span className='ubeeel-description'>{originalDevice?.Description}</span>
                            {(originalDevice?.Users?.length ?? 0) > 1 ? (
                                <a
                                    className={`ube-entry-number-of-users ${isEditExisting ? 'clickable' : ''}`}
                                    onClick={(): void => {
                                        setIsEditExisting(false)
                                        setValue(userEmailFieldName, EMPTY_STRING)
                                        setValue(userTypeFieldName, UserTypes.ESSENTIALS)
                                        setAdditionalSubscriptionTypes(undefined)
                                    }}
                                >
                                    {`(${originalDevice?.Users?.length ?? 0})`}
                                </a>
                            ) : (
                                <></>
                            )}
                        </div>
                        {telsysType === TelSysTypes.MS_TEAMS && originalDevice?.LicenceType ? (
                            <div
                                className='licence-type-trigger'
                                ref={licenceTypePopperRef}
                                onMouseOver={(): void => setLicenceTypePopperOpen(true)}
                                onMouseOut={(): void => setLicenceTypePopperOpen(false)}
                            >
                                <Icon iconName={IconName.INFO} />
                                <PopperBubble
                                    content={
                                        <>
                                            {language.get(`UserByExtension_LicenceTypes_${originalDevice.LicenceType}`)}
                                        </>
                                    }
                                    open={licenceTypePopperOpen}
                                    anchorEl={licenceTypePopperRef.current}
                                    infoMode
                                    placement='bottom'
                                />
                            </div>
                        ) : (
                            <></>
                        )}
                        {(originalDevice?.Users?.length ?? 0) <= 1 || isEditExisting ? (
                            <PopperBubble
                                anchorEl={popperRef.current}
                                open={isPopperOpen}
                                content={
                                    <div className='ube-username-display'>
                                        <div className='ube-username-display-title'>
                                            {language.get(UsersByExtension.UsersByExtensions_Username_Display_Title)}
                                        </div>
                                        <div>
                                            {IsStringEmpty(usernameForUser.current)
                                                ? LanguageUtils.formatLanguageEntry(
                                                      language.get(
                                                          UsersByExtension.UsersByExtensions_Username_Display_Title_No_Username
                                                      )
                                                  )
                                                : usernameForUser.current}
                                        </div>
                                    </div>
                                }
                                infoMode
                                placement='bottom'
                            />
                        ) : (
                            <></>
                        )}
                    </div>
                    {(originalDevice?.Users?.length ?? 0) > 1 && !isEditExisting ? (
                        <div className='ube-users-list'>
                            {originalDevice?.Users?.map(
                                (user: string, i: number): ReactElement => (
                                    <div
                                        className={`ube-user ${confirmDeleteIndex === i ? 'ube-confirm-delete' : ''}`}
                                        onDoubleClick={(): void => fetchUser(user, true)}
                                        key={`${user}-${i}`}
                                    >
                                        <span className='ubeu-name'>{user}</span>
                                        <Button
                                            className='ube-entry-action'
                                            onClick={(): void => fetchUser(user, true)}
                                        >
                                            <Icon iconName={IconName.PENCIL} />
                                        </Button>
                                        <EntryRemove
                                            currentIndex={currentIndex}
                                            user={user}
                                            originalDevice={originalDevice}
                                            updateDeviceUsers={updateDeviceUsers}
                                            updateExtensionErrors={updateExtensionErrors}
                                            isConfirmDelete={confirmDeleteIndex === i}
                                            setConfirmDelete={(isConfirmDelete: boolean): void =>
                                                setConfirmDeleteIndex(isConfirmDelete ? i : undefined)
                                            }
                                        />
                                    </div>
                                )
                            )}
                        </div>
                    ) : (
                        <div className='ube-entry-right'>
                            <div className='ube-entry-fields'>
                                {telsysType === TelSysTypes.MS_TEAMS && originalDevice?.LicenceType ? (
                                    <div className='ube-entry-field ms-licence'>
                                        {language.get(`UserByExtension_LicenceTypes_${originalDevice.LicenceType}`)}
                                    </div>
                                ) : (
                                    <></>
                                )}
                                <div className='ube-entry-field'>
                                    <TextInput
                                        className={
                                            !isEmailValid &&
                                            (dirtyFields?.users?.[currentIndex]?.email || isEditExisting)
                                                ? 'ube-invalid'
                                                : ''
                                        }
                                        inputName={userEmailFieldName}
                                        placeholder={language.get(UsersByExtension.UsersByExtensions_UserFields_Email)}
                                        rules={{
                                            required: true
                                        }}
                                        disabled={isDataLoading || emailInputDisabled}
                                        adornmentEnd={
                                            <>
                                                {emailInputDisabled ? (
                                                    <IconButton
                                                        customClassName='ube-email-input-end-adornment'
                                                        type={IconButtonType.BUTTON}
                                                        iconName={IconName.PENCIL}
                                                        onClick={(): void => setIsEditExisting(true)}
                                                    />
                                                ) : (
                                                    <></>
                                                )}
                                                {!emailInputDisabled &&
                                                userTypeDefaults?.allowEmailAsUsername &&
                                                isEmailValid &&
                                                userEmailValue !== idOnTelSysToUser ? (
                                                    <Checkbox
                                                        name={useEmailAsUsernameFieldName}
                                                        disabled={isDataLoading}
                                                        popupHover={
                                                            <>
                                                                {language.get(
                                                                    UsersByExtension.UsersByExtensions_UserFields_UseEmailAsUser
                                                                )}
                                                            </>
                                                        }
                                                    />
                                                ) : (
                                                    <></>
                                                )}
                                            </>
                                        }
                                        onKeyDown={submitOnCtrlEnter}
                                    />
                                </div>
                                {shouldShowWelcomeMailColumn ? (
                                    <div className='ube-entry-field ube-entry-field-mail'>
                                        {isCurrentUserDelete && originalDevice ? (
                                            <IconButton
                                                type={IconButtonType.BUTTON}
                                                iconName={IconName.EMAIL}
                                                isEnabled={!isCxiOnlyUser}
                                                title={language.getInterpolated(
                                                    UsersByExtension.SendWelcomeEmailTitle,
                                                    userEmailValue
                                                )}
                                                onClick={(): void => {
                                                    displayPromptPromise(
                                                        language.getInterpolated(
                                                            UsersByExtension.SendWelcomeEmailConfirm,
                                                            userEmailValue
                                                        )
                                                    ).then((): void => {
                                                        sendEmailForUser(usernameForUser.current, EmailType.WELCOME)
                                                            .then((): void => {
                                                                displaySuccessMessage(
                                                                    language.getInterpolated(
                                                                        UsersByExtension.EmailSuccessMessage,
                                                                        userEmailValue
                                                                    )
                                                                )
                                                            })
                                                            .catch((): void => {
                                                                displayErrorMessage(
                                                                    language.get(UsersByExtension.EmailFailMessage)
                                                                )
                                                            })
                                                    })
                                                }}
                                            />
                                        ) : (
                                            <Checkbox disabled={isCxiOnlyUser} name={welcomeEmailFieldName} />
                                        )}
                                    </div>
                                ) : (
                                    <></>
                                )}
                                {shouldShowReportingTypeColumn && (
                                    <div className='ube-entry-field'>
                                        <Select
                                            classNames={!isUserTypeValid ? 'ube-invalid' : ''}
                                            name={userTypeFieldName}
                                            options={currentSubscriptionOptions}
                                            renderSelectedValue={false}
                                            rules={{
                                                required: true
                                            }}
                                            disabled={isDataLoading}
                                            onKeyDown={submitOnCtrlEnter}
                                            handleChange={(value: string): void => {
                                                if (
                                                    isMounted.current &&
                                                    value !== ReportingAccessLevels.ENTERPRISE &&
                                                    value !== ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
                                                ) {
                                                    setValue(USE_EMAIL_AS_USERNAME_FIELD_NAME, false)
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                                {createCXIntegrationColumn()}
                            </div>
                            <div className='ube-entry-actions'>
                                {isCurrentUserDelete && originalDevice ? (
                                    <EntryRemove
                                        currentIndex={currentIndex}
                                        user={currentUsername.current ?? EMPTY_STRING}
                                        originalDevice={originalDevice}
                                        updateDeviceUsers={updateDeviceUsers}
                                        updateExtensionErrors={updateExtensionErrors}
                                        isConfirmDelete={confirmDelete}
                                        setConfirmDelete={setConfirmDelete}
                                        isLoading={isDataLoading}
                                        onRemove={(): void => {
                                            if (telsysType !== TelSysTypes.MS_TEAMS) {
                                                setValue(userEmailFieldName, EMPTY_STRING)
                                            }

                                            let defaultType: string = isAgent
                                                ? UserTypes.CALL_CENTRE_AGENT
                                                : UserTypes.ESSENTIALS

                                            if (telsysType === TelSysTypes.MS_TEAMS) {
                                                defaultType = UserTypes.NONE
                                            }

                                            updateLicenceSummary()

                                            setValue(userTypeFieldName, defaultType) //eslint-disable-line
                                            setCurrentEmail(undefined)
                                            setCurrentType(undefined)
                                            setCurrentUsername(undefined)
                                        }}
                                    />
                                ) : (
                                    <>
                                        <Button
                                            className='ube-entry-action'
                                            onMouseOver={(): void => setCheckboxPopperOpen(true)}
                                            onMouseOut={(): void => setCheckboxPopperOpen(false)}
                                            ref={checkboxPopperRef}
                                            disabled={
                                                !isEmailValid ||
                                                !isUserTypeValid ||
                                                isDataLoading ||
                                                (!useEmailAsUsernameValue && IsStringEmpty(idOnTelSysToUser)) ||
                                                (telsysType === TelSysTypes.MS_TEAMS &&
                                                    isMonitoredDeactivated &&
                                                    !isTypeTouched &&
                                                    !isCrmTouched)
                                            }
                                            onClick={(): void => {
                                                addModifyUser()
                                            }}
                                        >
                                            <Icon iconName={IconName.CHECK} />
                                        </Button>
                                        {!isDataLoading ? (
                                            <PopperBubble
                                                content={
                                                    <>
                                                        {language.get(
                                                            UsersByExtension.UsersByExtensions_UserFields_Checkbox_Tooltip
                                                        )}
                                                    </>
                                                }
                                                open={checkboxPopperOpen}
                                                anchorEl={checkboxPopperRef.current}
                                                infoMode
                                                placement='top'
                                            />
                                        ) : (
                                            <></>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {isAgent &&
                currentType !== userTypeValue &&
                (userTypeValue === UserTypes.ESSENTIALS ||
                    userTypeValue === UserTypes.PROFESSIONAL ||
                    userTypeValue === UserTypes.ENTERPRISE) ? (
                    <div className='ube-agent-warning'>
                        {language.get(UsersByExtension.UsersByExtension_UserType_IsNotAgent)}
                    </div>
                ) : (
                    <></>
                )}
                {telsysType !== TelSysTypes.MS_TEAMS &&
                currentType !== userTypeValue &&
                userTypeValue === UserTypes.NONE ? (
                    <div className='ube-agent-warning'>
                        {language.get(UsersByExtension.UsersByExtension_UserType_IsNone)}
                    </div>
                ) : (
                    <></>
                )}
                {!IsStringEmpty(extensionError) ? <div className='ube-error-message'>{extensionError}</div> : <></>}
            </div>
            {isDataLoading ? <Skeleton /> : <></>}
        </>
    )
}
